import type { Currency, Fund, NavDailyPoint } from '@nav/shared';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

import { Card } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingRegion, Skeleton } from '@/components/ui/states';
import { isMockMode, navDailyCsvUrl } from '@/lib/api';
import { EM_DASH, formatIsoDate, formatNav, formatPctPlain, formatTimestamp } from '@/lib/format';
import { BRAND } from '../palette';
import { sflShortName } from '../sflProducts';

/**
 * `NAV Harian` — the recent daily NAV rows plus the CSV export.
 *
 * Ported verbatim: the card header, the CSV button treatment (light red tint
 * on brand red), and the zebra striping.
 *
 * Changed — deliberately:
 *   The reference built the CSV in the browser from its in-memory array
 *   (`new Blob([...]) + URL.createObjectURL`). That can only ever export what
 *   the page happens to be holding — here, the most recent 30 rows. The export
 *   is now a plain link to `ApiRoutes.exportNavDailyCsv(code)`, so the file
 *   always matches the database rather than the viewport.
 *
 *   The `Fund` column showed a hardcoded alias ("Rupiah"/"Dollar") derived from
 *   the currency; it now shows the fund's own code, which is what the export
 *   contains.
 *
 *   Added a fifth column, `% Growth`: day-on-day change vs the previous row
 *   (`row.changePct`, computed server-side — see `navDailyRepo.ts`), styled
 *   with the same green/red trend colour as the monthly table's MoM/YoY cells.
 */

function pctColor(value: number | null): string {
  if (value == null) return BRAND.textFaint;
  if (value > 0) return BRAND.mint;
  if (value < 0) return BRAND.coral;
  return BRAND.ink;
}

function ExportButton({ fund, disabled }: { fund: Fund | null; disabled: boolean }) {
  const label = 'Unduh NAV harian sebagai CSV';

  // Fixture mode has no server behind it — say so instead of 404-ing the user.
  if (isMockMode()) {
    return (
      <button
        type="button"
        onClick={() =>
          toast.info('Export CSV memerlukan server API', {
            description: 'Halaman ini sedang berjalan dengan data contoh (?mock=1).',
          })
        }
        aria-label={label}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
        style={{ background: BRAND.redTint, color: BRAND.red }}
      >
        <Download className="size-3.5" aria-hidden="true" />
        CSV
      </button>
    );
  }

  if (!fund || disabled) {
    return (
      <button
        type="button"
        disabled
        aria-label={label}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold opacity-50 cursor-not-allowed"
        style={{ background: '#F1F5F9', color: '#94A3B8' }}
      >
        <Download className="size-3.5" aria-hidden="true" />
        CSV
      </button>
    );
  }

  return (
    <a
      href={navDailyCsvUrl(fund.code)}
      download={`nav_daily_${fund.code.toLowerCase()}.csv`}
      aria-label={label}
      onClick={() => toast.success('Menyiapkan unduhan CSV…')}
      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all no-underline"
      style={{ background: BRAND.redTint, color: BRAND.red }}
    >
      <Download className="size-3.5" aria-hidden="true" />
      CSV
    </a>
  );
}

export function DailyNavTable({
  fund,
  points,
  isLoading,
  error,
  onRetry,
}: {
  fund: Fund | null;
  points: NavDailyPoint[];
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const currency: Currency = fund?.currency ?? 'IDR';

  const header = (
    <div
      className="px-5 pt-5 pb-3 flex items-start justify-between gap-3"
      style={{ borderBottom: `1px solid ${BRAND.panelBorder}` }}
    >
      <div className="min-w-0">
        <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-display)', color: BRAND.ink }}>
          NAV Harian
        </h3>
        <p className="text-xs mt-0.5" style={{ color: BRAND.textFaint }}>
          {points.length > 0 ? `${points.length} hari terakhir` : 'Data harian per produk'}
        </p>
      </div>
      <ExportButton fund={fund} disabled={points.length === 0} />
    </div>
  );

  if (isLoading) {
    return (
      <Card>
        {header}
        <LoadingRegion label="Memuat NAV harian" className="p-4">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <Skeleton height={16} radius={5} />
            </div>
          ))}
        </LoadingRegion>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        {header}
        <ErrorState error={error} onRetry={onRetry} compact />
      </Card>
    );
  }

  if (points.length === 0) {
    return (
      <Card>
        {header}
        <EmptyState message="Belum ada data NAV harian untuk fund ini." compact />
      </Card>
    );
  }

  return (
    <Card>
      {header}
      <div className="overflow-x-auto no-scrollbar">
        <table className="w-full text-xs border-collapse">
          <caption className="sr-only">NAV harian terbaru untuk fund {fund?.code ?? ''}</caption>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['Tanggal', 'Produk', 'NAV', '% Growth', 'Diperbarui'].map((h) => (
                <th
                  key={h}
                  scope="col"
                  className="px-4 py-3 text-left font-medium whitespace-nowrap"
                  style={{
                    color: BRAND.textFaint,
                    fontSize: 11,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: `1px solid ${BRAND.panelBorder}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {points.map((row, i) => (
              <tr key={row.date} style={{ background: i % 2 === 0 ? '#fff' : '#FAFBFD' }}>
                <th
                  scope="row"
                  className="px-4 py-2.5 text-left font-normal whitespace-nowrap tabular-nums"
                  style={{ color: BRAND.ink, borderBottom: '1px solid rgba(27,36,54,0.07)' }}
                >
                  {formatIsoDate(row.date)}
                </th>
                <td
                  className="px-4 py-2.5 whitespace-nowrap"
                  style={{ color: BRAND.textDim, borderBottom: '1px solid rgba(27,36,54,0.07)' }}
                >
                  {fund ? sflShortName(fund.code) : EM_DASH}
                </td>
                <td
                  className="px-4 py-2.5 font-semibold whitespace-nowrap tabular-nums"
                  style={{ color: BRAND.ink, borderBottom: '1px solid rgba(27,36,54,0.07)' }}
                >
                  {formatNav(row.navValue, currency)}
                </td>
                <td
                  className="px-4 py-2.5 font-semibold whitespace-nowrap tabular-nums"
                  style={{ color: pctColor(row.changePct), borderBottom: '1px solid rgba(27,36,54,0.07)' }}
                >
                  {row.changePct == null ? EM_DASH : formatPctPlain(row.changePct)}
                </td>
                <td
                  className="px-4 py-2.5 whitespace-nowrap tabular-nums"
                  style={{ color: BRAND.textFaint, borderBottom: '1px solid rgba(27,36,54,0.07)' }}
                >
                  {formatTimestamp(row.loadedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
